import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';
import { useSeo } from '@/hooks/useSeo';
import { articles, categories, hotTrends } from '@/mocks/chigua';

const levelStyle: Record<string, string> = {
  爆: 'bg-primary-500 text-background-50',
  沸: 'bg-accent-500 text-background-50',
  热: 'bg-secondary-200 text-secondary-800',
  新: 'bg-background-200 text-foreground-600',
};

const platformColor: Record<string, string> = {
  微博: 'bg-primary-100 text-primary-800',
  抖音: 'bg-accent-100 text-accent-800',
  贴吧: 'bg-secondary-200 text-secondary-800',
};

export default function Hot() {
  useSeo({
    title: '今日瓜点热榜 · 围观指南',
    description:
      '吃瓜路线今日瓜点热榜，聚合微博、抖音、贴吧当下讨论度最高的热门话题与围观指南，一眼看懂大家都在聊什么。',
    keywords: '吃瓜路线今日瓜点,吃瓜路线热门话题,吃瓜路线讨论焦点,吃瓜路线围观指南',
  });

  const [params, setParams] = useSearchParams();
  const cat = params.get('cat') || 'all';
  const kw = params.get('kw') || '';
  const [keyword, setKeyword] = useState(kw);

  const setCat = (key: string) => {
    const next = new URLSearchParams(params);
    if (key === 'all') next.delete('cat');
    else next.set('cat', key);
    setParams(next, { replace: true });
  };

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    const k = keyword.trim();
    if (k) next.set('kw', k);
    else next.delete('kw');
    setParams(next, { replace: true });
  };

  const filteredTrends = hotTrends.filter((t) => {
    const matchCat = cat === 'all' || t.tag === categories.find((c) => c.key === cat)?.label;
    const matchKw = !kw || t.title.includes(kw);
    return matchCat && matchKw;
  });

  const activeLabel = categories.find((c) => c.key === cat)?.label || '全部';
  const filteredArticles = articles.filter((a) => {
    const matchCat = cat === 'all' || a.category === cat;
    const matchKw = !kw || a.title.includes(kw) || a.desc.includes(kw) || a.tags.some((t) => t.includes(kw));
    return matchCat && matchKw;
  });

  const collectionLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: '吃瓜路线 今日瓜点热榜',
    hasPart: filteredTrends.map((t, i) => ({ '@type': 'ListItem', position: i + 1, name: t.title })),
  };

  return (
    <>
      <JsonLd data={collectionLd} />
      <Navbar />
      <main>
        {/* 页头 */}
        <section className="border-b border-background-200 bg-background-100">
          <div className="container-x py-10 md:py-12">
            <nav className="flex items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑">
              <Link to="/" className="hover:text-primary-600">首页</Link>
              <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
              <span className="text-foreground-700">今日瓜点热榜</span>
            </nav>
            <div className="mt-5 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="inline-flex items-center gap-2 rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700">
                  <i className="ri-fire-line" aria-hidden="true"></i>
                  实时聚合微博 · 抖音 · 贴吧
                </p>
                <h1 className="mt-3 font-heading text-3xl text-foreground-900 md:text-4xl">
                  {cat === 'all' ? '今日瓜点热榜' : `${activeLabel} · 今日热点`}
                </h1>
                <p className="mt-2 max-w-xl text-sm leading-6 text-foreground-600">
                  {kw ? (
                    <>
                      关于「<strong className="text-primary-600">{kw}</strong>」的热门话题与围观指南如下，共{' '}
                      <strong className="text-primary-600">{filteredTrends.length + filteredArticles.length}</strong> 条结果。
                    </>
                  ) : (
                    '按热度整理当下最值得围观的话题，按栏目筛选，或直接搜索你关心的看点。'
                  )}
                </p>
              </div>
              <form onSubmit={submitSearch} className="relative w-full max-w-sm" role="search" aria-label="站内搜索">
                <i className="ri-search-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-base text-foreground-400" aria-hidden="true"></i>
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  type="search"
                  placeholder="搜索话题 / 看点"
                  className="w-full rounded-full border border-background-300 bg-background-50 py-3 pl-11 pr-20 text-sm text-foreground-800 outline-none placeholder:text-foreground-400 focus:border-primary-400"
                />
                <button type="submit" className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-full bg-primary-500 px-4 py-1.5 text-xs font-medium text-background-50 hover:bg-primary-600">
                  搜索
                </button>
              </form>
            </div>

            {/* 栏目筛选 */}
            <div className="mt-6 flex flex-wrap gap-2" role="tablist" aria-label="栏目筛选">
              {categories.map((c) => {
                const active = cat === c.key;
                return (
                  <button
                    key={c.key}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setCat(c.key)}
                    className={`rounded-full px-4 py-2 text-sm transition-colors ${
                      active
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-50 text-foreground-600 ring-1 ring-background-200 hover:bg-background-50 hover:text-primary-700'
                    }`}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        {/* 榜单 + 文章 */}
        <section className="container-x grid grid-cols-1 gap-8 py-10 md:py-14 lg:grid-cols-[0.95fr_1.05fr] lg:gap-10">
          {/* 趋势榜单 */}
          <div>
            <div className="flex items-center justify-between">
              <h2 className="flex items-center gap-2 font-heading text-xl text-foreground-900">
                <i className="ri-fire-line text-primary-500" aria-hidden="true"></i>
                当前热榜
              </h2>
              <span className="text-xs text-foreground-500">{filteredTrends.length} 条话题</span>
            </div>

            {filteredTrends.length === 0 ? (
              <div className="mt-4 rounded-3xl border border-dashed border-background-300 bg-background-100 p-10 text-center text-sm text-foreground-500">
                当前筛选下暂时没有热门话题，换个栏目试试。
              </div>
            ) : (
              <ol className="mt-4 space-y-3">
                {filteredTrends.map((t, i) => (
                  <li key={t.title} className="flex items-center gap-4 rounded-2xl border border-background-200 bg-background-50 px-4 py-3.5 transition-colors hover:border-primary-200">
                    <span className="w-7 shrink-0 text-center font-heading text-xl text-foreground-300">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span className="shrink-0 rounded-lg bg-background-100 px-1.5 py-0.5 text-[11px] text-foreground-500">{t.tag}</span>
                    <span className="min-w-0 flex-1 text-sm font-medium text-foreground-800">{t.title}</span>
                    <div className="flex shrink-0 flex-col items-end gap-1.5">
                      <span className="flex items-center gap-2">
                        <span className={`rounded-md px-1.5 py-0.5 text-[11px] font-bold ${levelStyle[t.level]}`}>{t.level}</span>
                        <span className={`rounded-md px-1.5 py-0.5 text-[11px] ${platformColor[t.platform] || ''}`}>{t.platform}</span>
                      </span>
                      <span className="text-[11px] text-foreground-500">{t.count}</span>
                    </div>
                  </li>
                ))}
              </ol>
            )}

            <div className="mt-6 rounded-2xl bg-accent-100/70 p-5 text-sm text-accent-900">
              <p className="flex items-center gap-2 font-medium">
                <i className="ri-lightbulb-flash-line" aria-hidden="true"></i>
                围观小贴士
              </p>
              <p className="mt-2 leading-6">
                榜单热度只是参考，「涨知识、看争议、聊观点」才是吃瓜的正确姿势。点开对应指南，看看大家到底在争什么吧。
              </p>
            </div>
          </div>

          {/* 围观指南结果 */}
          <div>
            <h2 className="flex items-center gap-2 font-heading text-xl text-foreground-900">
              <i className="ri-book-open-line text-accent-600" aria-hidden="true"></i>
              围观指南
            </h2>
            {filteredArticles.length === 0 ? (
              <div className="mt-4 rounded-3xl border border-dashed border-background-300 bg-background-100 p-10 text-center text-sm text-foreground-500">
                没有找到对应的指南，换个关键词或栏目试试。
              </div>
            ) : (
              <div className="mt-4 space-y-4">
                {filteredArticles.map((a) => (
                  <article key={a.id} className="group flex gap-4 rounded-2xl border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-200 md:p-4">
                    <Link to={`/post/${a.id}`} className="block w-28 shrink-0 overflow-hidden rounded-xl md:w-40" aria-label={a.title}>
                      <div className="aspect-[4/3] w-full overflow-hidden">
                        <img src={a.image} alt={a.title} title={`${a.title} - 吃瓜路线`} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      </div>
                    </Link>
                    <div className="flex min-w-0 flex-1 flex-col">
                      <div className="flex items-center gap-2">
                        <span className="rounded-md bg-accent-100 px-2 py-0.5 text-xs text-accent-800">{a.categoryLabel}</span>
                        <span className="text-xs text-foreground-400">{a.date}</span>
                      </div>
                      <h3 className="mt-1.5 text-base font-medium leading-snug text-foreground-900 md:text-lg">
                        <Link to={`/post/${a.id}`} className="transition-colors hover:text-primary-600">
                          {a.title}
                        </Link>
                      </h3>
                      <p className="mt-1 hidden text-sm leading-6 text-foreground-500 sm:line-clamp-2">{a.desc}</p>
                      <div className="mt-auto flex items-center justify-between pt-2 text-xs text-foreground-500">
                        <span className="inline-flex items-center gap-1">
                          <i className="ri-user-smile-line text-primary-400" aria-hidden="true"></i>
                          {a.author}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <i className="ri-eye-line" aria-hidden="true"></i>
                          {a.views}
                        </span>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}